import React, { useLayoutEffect } from 'react';
import { Box, Container, Typography, Button, Paper, Chip, Grid } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PaymentIcon from '@mui/icons-material/Payment';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import cashAppQR from '../assets/images/cashApp.jpeg';
import venmoQR from '../assets/images/Venmo.jpeg';

const RegistrationSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Scroll to top BEFORE the page paints (no visible scroll)
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Redirect if no state (direct URL access)
  if (!location.state?.racerId || !location.state?.raceType) {
    return <Navigate to="/register" replace />;
  }

  const { racerId, firstName, lastName, raceType, status } = location.state;
  const raceTypeName = raceType === 'half' ? 'Half Marathon' : 'Full Marathon';
  const fee = raceType === 'half' ? '$50' : '$55';

  return (
    <Box sx={{ py: { xs: 4, sm: 8 }, px: { xs: 0, sm: 0 }, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 6 },
            textAlign: 'center',
            borderRadius: { xs: 2, sm: 3 },
            border: '1px solid rgba(0,0,0,0.1)',
            background: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <CheckCircleOutlineIcon 
            sx={{ 
              fontSize: { xs: 60, sm: 80 }, 
              color: '#3FAF5D',
              mb: { xs: 2, sm: 3 }
            }} 
          />
          
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: '2rem', sm: '3.75rem' },
              lineHeight: { xs: 1.2, sm: 1.167 },
              background: 'linear-gradient(90deg, #3FAF5D 0%, #1C4C89 100%)',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            Registration Complete!
          </Typography>

          <Chip 
            label={status === 'registered' ? 'FULLY REGISTERED' : 'PENDING'}
            color={status === 'registered' ? 'success' : 'warning'}
            sx={{ mb: 3, fontWeight: 700, fontSize: '1rem', px: 2, py: 0.5 }}
          />

          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              mb: 4,
              bgcolor: 'rgba(63,175,93,0.1)',
              borderRadius: 3,
              border: '2px solid rgba(63,175,93,0.3)',
            }}
          >
            <EmojiEventsIcon sx={{ fontSize: 48, color: '#3FAF5D', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              Your Racer ID
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 900, 
                color: '#1C4C89',
                fontFamily: 'monospace',
                letterSpacing: 4,
                mb: 2
              }}
            >
              {racerId}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
              {firstName} {lastName}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {raceTypeName}
            </Typography>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              mb: 4,
              bgcolor: 'rgba(237,108,2,0.1)',
              borderRadius: 2,
              border: '2px solid rgba(237,108,2,0.3)',
              maxWidth: { xs: '100%', sm: '600px' },
              mx: 'auto'
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, color: '#D84315' }}>
              ⚠️ Payment Required
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
              Your registration is currently <strong>PENDING</strong>. Please complete your payment of <strong>{fee}</strong> to finalize your registration.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Once payment is received and verified, your status will be updated to <strong>REGISTERED</strong> and your name will appear on the Results page.
            </Typography>
          </Paper>

          <Paper
            elevation={3}
            sx={{
              p: 4,
              mb: 4,
              bgcolor: 'white',
              borderRadius: 3,
              border: '1px solid rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'center' }}>
              <PaymentIcon sx={{ mr: 1, color: 'primary.main', fontSize: 32 }} />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Complete Your Payment
              </Typography>
            </Box>
            
            <Typography variant="body1" paragraph sx={{ textAlign: 'center', mb: 1 }}>
              Please send your registration fee of <strong>{fee}</strong> via CashApp or Venmo.
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph sx={{ textAlign: 'center', mb: 4 }}>
              <strong>Important:</strong> Include your Racer ID <strong>({racerId})</strong> in the payment note.
            </Typography>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 3, 
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    border: '2px solid rgba(0,200,83,0.3)',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#00D54B' }}>
                    CashApp
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                    $Terry1748
                  </Typography>
                  <Box 
                    component="img" 
                    src={cashAppQR}
                    alt="CashApp QR Code"
                    sx={{ 
                      width: '100%',
                      maxWidth: 250,
                      height: 'auto',
                      my: 2,
                      borderRadius: 1,
                      border: '1px solid rgba(0,0,0,0.1)'
                    }}
                  />
                  <Button
                    variant="contained"
                    href="https://cash.app/$Terry1748"
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                    sx={{ 
                      mt: 'auto',
                      backgroundColor: '#00D54B',
                      color: '#000',
                      fontWeight: 700,
                      '&:hover': {
                        backgroundColor: '#00C043'
                      }
                    }}
                  >
                    Open CashApp
                  </Button>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 3, 
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    border: '2px solid rgba(0,140,255,0.3)',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#008CFF' }}>
                    Venmo
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                    @Terry-Phillips-43
                  </Typography>
                  <Box 
                    component="img" 
                    src={venmoQR}
                    alt="Venmo QR Code"
                    sx={{ 
                      width: '100%',
                      maxWidth: 250,
                      height: 'auto',
                      my: 2,
                      borderRadius: 1,
                      border: '1px solid rgba(0,0,0,0.1)'
                    }}
                  />
                  <Button
                    variant="contained"
                    href="https://venmo.com/u/Terry-Phillips-43"
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                    sx={{ 
                      mt: 'auto',
                      backgroundColor: '#008CFF',
                      fontWeight: 700,
                      '&:hover': {
                        backgroundColor: '#0077D6'
                      }
                    }}
                  >
                    Open Venmo
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
              What's Next:
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: { xs: 2, sm: 3 }, 
              mb: { xs: 3, sm: 4 },
              px: { xs: 1, sm: 0 },
              textAlign: 'left',
              maxWidth: { xs: '100%', sm: '600px' },
              mx: 'auto'
            }}>
              <Box>
                <Typography variant="h6" sx={{ mb: 1, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box component="span" sx={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: '#3FAF5D',
                    color: 'white',
                    fontWeight: 700
                  }}>
                    1
                  </Box>
                  Complete Payment
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', ml: 5 }}>
                  Send {fee} via CashApp or Venmo (see payment section above). Don't forget to include your Racer ID ({racerId}) in the payment note!
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" sx={{ mb: 1, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box component="span" sx={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: '#3FAF5D',
                    color: 'white',
                    fontWeight: 700
                  }}>
                    2
                  </Box>
                  Status Update
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', ml: 5 }}>
                  Once your payment is verified, your status will be changed from PENDING to REGISTERED, and your name will appear on the Results page.
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" sx={{ mb: 1, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box component="span" sx={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: '#3FAF5D',
                    color: 'white',
                    fontWeight: 700
                  }}>
                    3
                  </Box>
                  Race Day: November 9th, 2025
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', ml: 5 }}>
                  Make sure to arrive early, warm up, and get ready for an amazing race experience!
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" sx={{ mb: 1, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box component="span" sx={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: '#3FAF5D',
                    color: 'white',
                    fontWeight: 700
                  }}>
                    4
                  </Box>
                  Race Day & Official Results
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', ml: 5 }}>
                  Run the race on November 9th, 2025. Your official finish time and rank will be added to your Results page entry after the race concludes.
                </Typography>
              </Box>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                bgcolor: 'rgba(28,76,137,0.1)',
                borderRadius: 2,
                border: '1px solid rgba(28,76,137,0.2)',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                maxWidth: { xs: '100%', sm: '600px' },
                mx: 'auto'
              }}
            >
              <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}>
                📧 Important Notes:
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                • We will not be sending email confirmations. Please save your Racer ID ({racerId}).
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                • Your name will only appear on the Results page after payment is verified and status is updated to REGISTERED.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                • Payment verification typically takes 1-2 business days.
              </Typography>
            </Paper>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1.5, sm: 2 }, 
            justifyContent: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            '& .MuiButton-root': {
              width: { xs: '100%', sm: 'auto' }
            }
          }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/course')}
              sx={{
                py: 1.5,
                px: 4,
                background: 'linear-gradient(90deg, #3FAF5D 0%, #1C4C89 100%)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #1C4C89 0%, #3FAF5D 100%)',
                },
              }}
            >
              View Course Map
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/about')}
              sx={{
                py: 1.5,
                px: 4,
                borderColor: '#3FAF5D',
                color: '#3FAF5D',
                '&:hover': {
                  borderColor: '#1C4C89',
                  color: '#1C4C89',
                  bgcolor: 'rgba(63,175,93,0.05)',
                },
              }}
            >
              Race Information
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/')}
              sx={{
                py: 1.5,
                px: 4,
                borderColor: '#1C4C89',
                color: '#1C4C89',
                '&:hover': {
                  borderColor: '#3FAF5D',
                  color: '#3FAF5D',
                  bgcolor: 'rgba(28,76,137,0.05)',
                },
              }}
            >
              Return Home
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default RegistrationSuccess;